import React from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ExternalLink } from "lucide-react";

/**
 * Converts common Google Drive share URL shapes into their embeddable
 * "/preview" form (Drive's normal /view links refuse to render in an
 * iframe). Anything unrecognized is passed through as-is — the browser
 * will still attempt to render it, and the "Open in Drive" fallback link
 * below always works regardless.
 */
function toEmbedUrl(url) {
  if (!url) return url;
  const fileMatch = url.match(/drive\.google\.com\/file\/d\/([^/]+)/);
  if (fileMatch) return `https://drive.google.com/file/d/${fileMatch[1]}/preview`;
  const openMatch = url.match(/drive\.google\.com\/open\?id=([^&]+)/);
  if (openMatch) return `https://drive.google.com/file/d/${openMatch[1]}/preview`;
  return url;
}

/** Generic in-app preview for any Google Drive share URL (FIRC, PO scans,
 * signed contracts, etc.) — an iframe + a guaranteed "Open in Drive" escape
 * hatch since Drive permissions/sharing settings can still block embedding. */
export default function DriveLinkPreviewModal({ open, url, title, onClose }) {
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="rounded-sm max-w-3xl h-[80vh] flex flex-col gap-2" data-testid="drive-link-preview-modal">
        <DialogHeader className="flex-row items-center justify-between space-y-0">
          <DialogTitle className="font-display truncate pr-6">{title || "Document preview"}</DialogTitle>
        </DialogHeader>
        <div className="flex-1 min-h-0 bg-slate-100 rounded-sm overflow-hidden">
          {url ? (
            <iframe src={toEmbedUrl(url)} title={title || "Document preview"} className="w-full h-full border-0" data-testid="drive-link-preview-iframe" />
          ) : (
            <div className="h-full flex items-center justify-center text-sm text-slate-400">No document linked.</div>
          )}
        </div>
        {url && (
          <a href={url} target="_blank" rel="noreferrer" className="text-xs text-[#F54800] hover:underline inline-flex items-center gap-1 self-start" data-testid="drive-link-preview-external">
            <ExternalLink size={12} /> Open in Google Drive
          </a>
        )}
      </DialogContent>
    </Dialog>
  );
}
